/**
 * Route table — real, deep-linkable, scope-bearing paths (decision #9).
 *
 * Shape (docs/design/system-design.md sec 7):
 *   /login                                  BFF launcher (unauthenticated)
 *   /                                       -> redirect to first org
 *   /o/:orgId                               customer landing -> redirects to ./overview
 *   /o/:orgId/overview                      the same Overview, by name (Customer menu)
 *   /o/:orgId/dashboard                     customer-wide Health
 *   /o/:orgId/devices                       org-wide device list
 *   /o/:orgId/devices/:serial               device detail (Sheet over the list)
 *   /o/:orgId/clients | templates | audit   org-wide sections
 *   /o/:orgId/s/:siteId/dashboard           site health landing
 *   /o/:orgId/s/:siteId/devices[/:serial]   site device list + detail
 *   /o/:orgId/s/:siteId/clients|templates|audit
 *
 * The scope layers (org, then site) each go through ScopeSync so the URL is the
 * source of truth for the current tenancy scope. Section routes are declared
 * once (buildSections) and reused at both org and site level.
 *
 * Customer menu redesign (2026-10-07): nav LABELS changed (Audit -> Change log, Operators ->
 * Team, Network Users -> Wi-Fi users, ...) but every section SLUG below is unchanged, so no
 * bookmark breaks and no slug redirect is needed (lib/navModel LEGACY_SECTIONS + its test pin
 * this). The only new path is `overview`; under a site it redirects to the customer-level one.
 */
import { createBrowserRouter, Navigate, type RouteObject } from "react-router-dom";
import { RequireAuth } from "./routes/RequireAuth";
import { ScopeSync } from "./routes/ScopeSync";
import { RootRedirect } from "./routes/RootRedirect";
import { AppShell } from "@/components/layout/AppShell";
import Login from "./routes/Login";
import Customers from "./routes/Customers";
import OrgHome from "./routes/OrgHome";
import Overview from "./routes/Overview";
import Dashboard from "./routes/Dashboard";
import Devices from "./routes/Devices";
import DeviceDetail from "./routes/DeviceDetail";
import Clients from "./routes/Clients";
import ClientDetail from "./routes/ClientDetail";
import Templates from "./routes/Templates";
import SiteConfigure from "./routes/SiteConfigure";
import Onboard from "./routes/Onboard";
import Sites from "./routes/Sites";
import SSIDs from "./routes/SSIDs";
import SwitchPorts from "./routes/SwitchPorts";
import RadioSettings from "./routes/RadioSettings";
import Mesh from "./routes/Mesh";
import GuestPortal from "./routes/GuestPortal";
import Passpoint from "./routes/Passpoint";
import Vouchers from "./routes/Vouchers";
import MultiPsk from "./routes/MultiPsk";
import DeviceImport from "./routes/DeviceImport";
import Operators from "./routes/Operators";
import Alerts from "./routes/Alerts";
import NetworkUsers from "./routes/NetworkUsers";
import RadiusServers from "./routes/RadiusServers";
import Radius from "./routes/Radius";
import Notifications from "./routes/Notifications";
import Multicast from "./routes/Multicast";
import SwitchVlans from "./routes/SwitchVlans";
import GatewayVlans from "./routes/GatewayVlans";
import GatewayDhcp from "./routes/GatewayDhcp";
import GatewayContentFiltering from "./routes/GatewayContentFiltering";
import AccessControl from "./routes/AccessControl";
import FirewallPolicies from "./routes/FirewallPolicies";
import GatewayFirewall from "./routes/GatewayFirewall";
import PortProfiles from "./routes/PortProfiles";
import ApPortProfiles from "./routes/ApPortProfiles";
import Topology from "./routes/Topology";
import RfSpectrum from "./routes/RfSpectrum";
import Firmware from "./routes/Firmware";
import WalledGardens from "./routes/WalledGardens";
import SwitchPoe from "./routes/SwitchPoe";
import Pms from "./routes/Pms";
import Audit from "./routes/Audit";
import NotFound from "./routes/NotFound";
import ComingSoon from "./routes/ComingSoon";
import ClientUsage from "./routes/ClientUsage";
import FirmwareCompliance from "./routes/FirmwareCompliance";
import RouteTable from "./routes/RouteTable";
import GatewaySecurity from "./routes/GatewaySecurity";

/** The section routes shared by org-wide and site-scoped subtrees. `level` decides only the
 *  one section whose meaning differs by scope: the Overview (a table of every site). */
function buildSections(level: "customer" | "site"): RouteObject[] {
  return [
    // Customer Overview. Under a site it has no meaning ("a table of one site"), so the
    // site-level path widens back to the customer-level page instead of 404ing a bookmark.
    level === "customer"
      ? { path: "overview", element: <Overview /> }
      : { path: "overview", element: <Navigate to="../../overview" replace /> },
    { path: "dashboard", element: <Dashboard /> },
    // Open alerts list (OP#1854): every alert count in the UI links here, filtered by URL
    // (?severity=, ?kind=). Same page at customer and site scope.
    { path: "alerts", element: <Alerts /> },
    {
      path: "devices",
      element: <Devices />,
      // Detail is a child so the list stays mounted behind the Sheet.
      children: [{ path: ":serial", element: <DeviceDetail /> }],
    },
    // Kind-filtered device lists: Wireless shows APs, Switch shows switches.
    {
      path: "access-points",
      element: <Devices kindFilter="access_point" title="Access Points" section="access-points" />,
      children: [{ path: ":serial", element: <DeviceDetail /> }],
    },
    {
      path: "switches",
      element: <Devices kindFilter="switch" title="Switches" section="switches" />,
      children: [{ path: ":serial", element: <DeviceDetail /> }],
    },
    { path: "multicast", element: <Multicast /> },
    {
      path: "firewalls",
      element: <Devices kindFilter="firewall" title="Firewalls" section="firewalls" />,
      children: [{ path: ":serial", element: <DeviceDetail /> }],
    },
    // Whitebox uCentral gateways (x86 QOTOM-class): kind-filtered list + the same detail sheet.
    {
      path: "gateways",
      element: <Devices kindFilter="gateway" title="Gateways" section="gateways" />,
      children: [{ path: ":serial", element: <DeviceDetail /> }],
    },
    { path: "gateway-firewall", element: <GatewayFirewall /> },
    // Kept reachable by URL: the catalog-backed policy screen this replaced. It is
    // no longer in the nav because it reached no device (see GatewayFirewall.tsx).
    { path: "firewall-rules", element: <FirewallPolicies /> },
    {
      path: "clients",
      element: <Clients />,
      // "Where is this MAC?" sheet over the list (list stays mounted, same as devices).
      children: [{ path: ":mac", element: <ClientDetail /> }],
    },
    { path: "sites", element: <Sites /> },
    { path: "templates", element: <Templates /> },
    { path: "configure", element: <SiteConfigure /> },
    // Customer > Site services tabs (OP#1858): the VLANs and Mesh tabs live UNDER "configure" so
    // switching tabs never moves the menu highlight. Same screens as Gateway > Site VLANs and
    // Wireless > Mesh; SiteTabs shows the strip only on these paths.
    { path: "configure/vlans", element: <GatewayVlans /> },
    { path: "configure/mesh", element: <Mesh /> },
    // Site VLANs moved INTO Gateway > Configure > Site VLANs (owner request 2026-09-14). The old
    // path stays as a redirect so bookmarks keep working.
    { path: "site-vlans", element: <Navigate to="../gateway-vlans" replace /> },
    { path: "onboard", element: <Onboard /> },
    { path: "audit", element: <Audit /> },
    { path: "topology", element: <Topology /> },
    { path: "ssids", element: <SSIDs /> },
    { path: "port-profiles", element: <ApPortProfiles /> },
    { path: "access-control", element: <AccessControl /> },
    { path: "firewall", element: <FirewallPolicies shaping /> },
    { path: "radio", element: <RadioSettings /> },
    // Site Wi-Fi mesh (wireless backhaul over an AP group), OP#1767.
    { path: "mesh", element: <Mesh /> },
    { path: "multi-psk", element: <MultiPsk /> },
    { path: "device-import", element: <DeviceImport /> },
    { path: "operators", element: <Operators /> },
    { path: "network-users", element: <NetworkUsers /> },
    { path: "radius-servers", element: <RadiusServers /> },
    { path: "radius-realms", element: <Radius /> },
    { path: "notifications", element: <Notifications /> },
    // Hospitality
    { path: "usage", element: <ClientUsage /> },
    { path: "firmware-compliance", element: <FirmwareCompliance /> },
    { path: "guest-portal", element: <GuestPortal /> },
    { path: "passpoint", element: <Passpoint /> },
    { path: "vouchers", element: <Vouchers /> },
    { path: "pms", element: <Pms /> },
    { path: "walled-gardens", element: <WalledGardens /> },
    { path: "rf-spectrum", element: <RfSpectrum /> },
    { path: "switch-ports", element: <SwitchPorts /> },
    { path: "switch-vlans", element: <SwitchVlans /> },
    { path: "switch-poe", element: <SwitchPoe /> },
    { path: "switch-portprofiles", element: <PortProfiles /> },
    { path: "firmware", element: <Firmware /> },
    // Security Posture (formerly the "security-center" ComingSoon): a read-only aggregation of
    // the firewall/routes/watchdog reads, NOT an IDS event feed the gateway cannot produce.
    { path: "security-center", element: <GatewaySecurity /> },
    // Gateway screens that are not built. They route to ComingSoon rather than 404 so the
    // product's shape is visible and each one states honestly what it still needs -- the
    // `status: "soon"` contract that navItems has declared since it was written but that
    // nothing was wired to until now.
    { path: "route-table", element: <RouteTable /> },
    // Gateway VLANs (= the SITE VLAN table + rollout) and Gateway DHCP are BUILT, both on the ONE gateway config write path
    // (services/gateway_config): DHCP is three `dhcp.*` intents on that registry, not a
    // second pipeline. Routing still routes to ComingSoon -- it is blocked on firmware (the
    // uCentral schema has no static-route object), and its ComingSoon copy says so.
    { path: "gateway-vlans", element: <GatewayVlans /> },
    { path: "gateway-dhcp", element: <GatewayDhcp /> },
    { path: "gateway-routing", element: <ComingSoon /> },
    { path: "traffic-shaping", element: <ComingSoon /> },
    { path: "threat-protection", element: <ComingSoon /> },
    // Content Filtering is BUILT as a curated front end over the DHCP `use-dns` field
    // (the `dhcp.scope` intent on the existing gateway write path -- no new endpoint).
    // It is DNS-layer only; true category/URL filtering still needs a feed (see the page).
    { path: "content-filtering", element: <GatewayContentFiltering /> },
    { path: "gateway-access-control", element: <ComingSoon /> },
  ];
}

export const router = createBrowserRouter([
  { path: "/login", element: <Login /> },
  {
    // Every child below is behind auth (/api/v2/me hydration + 401 guard).
    element: <RequireAuth />,
    children: [
      { path: "/", element: <RootRedirect /> },
      {
        element: <AppShell />,
        children: [
          // MSP portal landing: the list of customers (orgs). Top of the funnel.
          { path: "customers", element: <Customers /> },
          {
            // Org scope layer.
            path: "o/:orgId",
            element: <ScopeSync />,
            children: [
              { index: true, element: <OrgHome /> },
              ...buildSections("customer"),
              {
                // Site scope layer (nested — inherits :orgId).
                path: "s/:siteId",
                element: <ScopeSync />,
                children: [
                  { index: true, element: <Navigate to="dashboard" replace /> },
                  ...buildSections("site"),
                ],
              },
            ],
          },
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
]);
